import { useCallback, useEffect, useState } from "react";
import { Heart } from "lucide-react";
import { container } from "tsyringe";
import CommentResponseResource from "common/resources/Project/Config/Forum/Comment/CommentResponseResource";
import Button, { EButtonColor, EButtonVariant } from "components/elements/Button";
import { useProjectId } from "hooks/useProjectId";
import CommentLikeService from "services/projects/CommentLikeService";
import classes from "./classes.module.scss";
import { usePostId } from "hooks/usePostId";

type IProps = {
	comment: Pick<CommentResponseResource, "id">;
};

const commentLikeService = container.resolve(CommentLikeService);

export default function CommentLikeButton(props: IProps) {
	const { isLiked, toggleLike, getLikesCount } = useLike(props.comment);

	const handleClickToggleLike = useCallback(
		async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
			e.stopPropagation();
			e.preventDefault();

			return toggleLike();
		},
		[toggleLike],
	);

	return (
		<Button variant={isLiked ? EButtonVariant.CONTAINED : EButtonVariant.OUTLINED} color={EButtonColor.PRIMARY} className={classes["root"]} onClick={handleClickToggleLike}>
			<Heart fill={isLiked ? "black" : ""} className={classes["icon"]} />
			{getLikesCount()}
		</Button>
	);
}

const useLike = (comment: Pick<CommentResponseResource, "id">) => {
	const { projectId } = useProjectId();
	const { postId } = usePostId();

	const [previouslyLikedCount, setPreviouslyLikedCount] = useState<number>(0);
	const [isPreviouslyLiked, setIsPreviouslyLiked] = useState<boolean>(false);
	const [isLiked, setIsLiked] = useState<boolean>(false);

	const toggleLike = useCallback(async () => {
		return commentLikeService[isLiked ? "unlike" : "like"](projectId, postId, comment.id).then(() => {
			setIsLiked((isLiked) => !isLiked);
		});
	}, [comment.id, isLiked, postId, projectId]);

	const getLikesCount = useCallback(() => {
		return isLiked ? previouslyLikedCount + (isPreviouslyLiked ? 0 : 1) : previouslyLikedCount - (isPreviouslyLiked ? 1 : 0);
	}, [isLiked, isPreviouslyLiked, previouslyLikedCount]);

	const fetchLikesCount = useCallback(() => {
		commentLikeService.count(projectId, postId, comment.id).then(({ count }) => setPreviouslyLikedCount(count));
	}, [comment.id, postId, projectId]);

	const fetchIsPreviouslyLiked = useCallback(() => {
		commentLikeService.isLiked(projectId, postId, comment.id).then(({ isLiked }) => {
			setIsLiked(isLiked);
			setIsPreviouslyLiked(isLiked);
		});
	}, [comment.id, postId, projectId]);

	useEffect(() => {
		fetchIsPreviouslyLiked();
	}, [fetchIsPreviouslyLiked]);

	useEffect(() => {
		fetchLikesCount();
	}, [fetchLikesCount]);

	return {
		isLiked,
		toggleLike,
		getLikesCount,
	};
};
